import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';

// Tipos para notificaciones
interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  title?: string;
}

// Query para obtener facturas susceptibles con filtro de fecha
const GET_SUSCEPTIBLE_INVOICES = gql`
  query GetSusceptibleInvoices($startDate: DateTime, $endDate: DateTime) {
    getSusceptibleInvoices(startDate: $startDate, endDate: $endDate) {
      id
      invoiceNumber
      customerName
      customerDocument
      total
      createdAt
    }
  }
`;

// Mutation para eliminar
const DELETE_INVOICES = gql`
  mutation DeleteInvoices($input: DeleteInvoicesInput!) {
    deleteInvoicesManually(input: $input)
  }
`;

interface AuthorizationPanelProps {
  onComplete?: () => void;
}

type SelectionMode = 'amount' | 'percentage' | 'count' | 'manual';

// Función para formatear fecha/hora para input datetime-local
const formatDateTimeForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Obtener inicio del día - por defecto último mes para ver facturas históricas
const getStartOfDay = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1); // Último mes por defecto
  date.setHours(0, 0, 0, 0);
  return date;
};

// Obtener fin del día actual
const getEndOfDay = () => {
  const date = new Date();
  date.setHours(23, 59, 59, 999);
  return date;
};

export const AuthorizationPanel: React.FC<AuthorizationPanelProps> = ({ onComplete }) => {
  // Sistema de notificaciones
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Función para mostrar notificaciones
  const showNotification = useCallback((type: Notification['type'], message: string, title?: string) => {
    const id = Date.now().toString();
    const notification: Notification = { id, type, message, title };
    setNotifications(prev => [...prev, notification]);

    // Auto-remover después de 4 segundos
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 4000);
  }, []);

  // Función para cerrar notificación manualmente
  const closeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Estado para el filtro de fechas
  const [filterStartDate, setFilterStartDate] = useState<Date>(getStartOfDay());
  const [filterEndDate, setFilterEndDate] = useState<Date>(getEndOfDay());
  const [filterApplied, setFilterApplied] = useState(false);

  // Query con filtros de fecha
  const { data, loading, refetch } = useQuery(GET_SUSCEPTIBLE_INVOICES, {
    variables: {
      startDate: filterStartDate.toISOString(),
      endDate: filterEndDate.toISOString(),
    },
    fetchPolicy: 'cache-and-network',
  });

  const [deleteInvoices, { loading: deleting }] = useMutation(DELETE_INVOICES);

  const [selectionMode, setSelectionMode] = useState<SelectionMode | null>(null);
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [reason, setReason] = useState('');

  // Parámetros de selección
  const [targetAmount, setTargetAmount] = useState<number>(0);
  const [targetPercentage, setTargetPercentage] = useState<number>(0);
  const [targetCount, setTargetCount] = useState<number>(0);

  const susceptibleInvoices = data?.getSusceptibleInvoices || [];
  const totalAvailable = useMemo(() =>
    susceptibleInvoices.reduce((sum: number, inv: any) => sum + Number(inv.total), 0),
    [susceptibleInvoices]
  );

  // Limpiar selección cuando cambian los filtros
  useEffect(() => {
    setSelectedInvoices(new Set());
  }, [filterStartDate, filterEndDate]);

  // Aplicar filtro de fechas
  const handleApplyFilter = () => {
    setFilterApplied(true);
    setSelectedInvoices(new Set());
    refetch({
      startDate: filterStartDate.toISOString(),
      endDate: filterEndDate.toISOString(),
    });
  };

  // Presets de fecha rápidos
  const setTodayFilter = () => {
    setFilterStartDate(getStartOfDay());
    setFilterEndDate(getEndOfDay());
  };

  const setYesterdayFilter = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const start = new Date(yesterday);
    start.setHours(0, 0, 0, 0);
    const end = new Date(yesterday);
    end.setHours(23, 59, 59, 999);
    setFilterStartDate(start);
    setFilterEndDate(end);
  };

  const setLastWeekFilter = () => {
    const start = new Date();
    start.setDate(start.getDate() - 7);
    start.setHours(0, 0, 0, 0);
    setFilterStartDate(start);
    setFilterEndDate(getEndOfDay());
  };

  const setLastMonthFilter = () => {
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    start.setHours(0, 0, 0, 0);
    setFilterStartDate(start);
    setFilterEndDate(getEndOfDay());
  };

  // Aplicar selección automática según el modo
  const handleApplySelection = () => {
    if (susceptibleInvoices.length === 0) return;

    let selected: string[] = [];

    switch (selectionMode) {
      case 'amount':
        // Seleccionar por monto específico
        // Solo agregar facturas si el total no supera el monto objetivo
        let accumulatedAmount = 0;
        for (const inv of susceptibleInvoices) {
          const invoiceTotal = Number(inv.total);
          // Verificar si agregar esta factura superaría el límite
          if (accumulatedAmount + invoiceTotal > targetAmount) {
            // Si ya tenemos algo seleccionado, no agregar más
            // Si no tenemos nada y esta factura es <= targetAmount, agregarla
            if (selected.length === 0 && invoiceTotal <= targetAmount) {
              selected.push(inv.id);
              accumulatedAmount += invoiceTotal;
            }
            // Continuar buscando facturas más pequeñas que quepan
            continue;
          }
          selected.push(inv.id);
          accumulatedAmount += invoiceTotal;
        }
        break;

      case 'percentage':
        // Seleccionar por porcentaje de ventas
        const targetAmountFromPercentage = (totalAvailable * targetPercentage) / 100;
        let accumulated = 0;
        for (const inv of susceptibleInvoices) {
          const invoiceTotal = Number(inv.total);
          // Verificar si agregar esta factura superaría el límite
          if (accumulated + invoiceTotal > targetAmountFromPercentage) {
            // Si ya tenemos algo seleccionado, no agregar más
            // Si no tenemos nada y esta factura es <= target, agregarla
            if (selected.length === 0 && invoiceTotal <= targetAmountFromPercentage) {
              selected.push(inv.id);
              accumulated += invoiceTotal;
            }
            // Continuar buscando facturas más pequeñas que quepan
            continue;
          }
          selected.push(inv.id);
          accumulated += invoiceTotal;
        }
        break;

      case 'count':
        // Seleccionar por cantidad específica
        selected = susceptibleInvoices.slice(0, targetCount).map((inv: any) => inv.id);
        break;

      case 'manual':
        // No hacer nada, selección manual
        return;
    }

    setSelectedInvoices(new Set(selected));
  };

  const handleToggleInvoice = (invoiceId: string) => {
    const newSelection = new Set(selectedInvoices);
    if (newSelection.has(invoiceId)) {
      newSelection.delete(invoiceId);
    } else {
      newSelection.add(invoiceId);
    }
    setSelectedInvoices(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedInvoices.size === susceptibleInvoices.length) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(susceptibleInvoices.map((inv: any) => inv.id)));
    }
  };

  const calculateTotalSelected = () => {
    if (!susceptibleInvoices) return 0;
    return susceptibleInvoices
      .filter((inv: any) => selectedInvoices.has(inv.id))
      .reduce((sum: number, inv: any) => sum + Number(inv.total), 0);
  };

  // Abrir modal de confirmación
  const handleOpenConfirmModal = () => {
    if (selectedInvoices.size === 0) {
      showNotification('warning', 'Debe seleccionar al menos una factura', 'Selección requerida');
      return;
    }
    setShowConfirmModal(true);
  };

  // Ejecutar eliminación desde el modal
  const handleAuthorize = async () => {
    try {
      await deleteInvoices({
        variables: {
          input: {
            invoiceIds: Array.from(selectedInvoices),
            reason: reason || undefined,
          },
        },
      });

      showNotification('success', `Se eliminaron ${selectedInvoices.size} facturas por un total de ${formatCurrency(calculateTotalSelected())}`, 'Eliminación exitosa');
      setSelectedInvoices(new Set());
      setReason('');
      setShowConfirmModal(false);
      refetch();
      onComplete?.();
    } catch (error) {
      console.error('Error:', error);
      showNotification('error', 'Ocurrió un error al eliminar las facturas. Intente nuevamente.', 'Error de eliminación');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString('es-CO');
  };

  const formatFilterRange = () => {
    const startStr = filterStartDate.toLocaleString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    const endStr = filterEndDate.toLocaleString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${startStr} - ${endStr}`;
  };

  return (
    <div className="authorization-panel">
      <div className="panel-header warning-header">
        <h2>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline', verticalAlign: 'middle', marginRight: '8px'}}>
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          Panel de Autorización de Eliminación
        </h2>
        <p className="warning-text">
          Las facturas seleccionadas serán ELIMINADAS PERMANENTEMENTE de la base principal.
          Se mantendrá copia en la base espejo para auditoría.
        </p>
      </div>

      {/* Filtro de Rango de Fechas y Horas */}
      <div className="card filter-card">
        <h3>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline', verticalAlign: 'middle', marginRight: '6px'}}>
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          Filtrar por Rango de Fechas y Horas
        </h3>
        <p className="card-description">
          Primero seleccione el rango de fechas y horas para filtrar las facturas susceptibles de eliminación.
        </p>

        <div className="date-filter-container">
          <div className="date-filter-inputs">
            <div className="date-input-group">
              <label>Fecha y Hora Inicio:</label>
              <input
                type="datetime-local"
                value={formatDateTimeForInput(filterStartDate)}
                onChange={(e) => setFilterStartDate(new Date(e.target.value))}
                className="datetime-input"
              />
            </div>
            <div className="date-input-group">
              <label>Fecha y Hora Fin:</label>
              <input
                type="datetime-local"
                value={formatDateTimeForInput(filterEndDate)}
                onChange={(e) => setFilterEndDate(new Date(e.target.value))}
                className="datetime-input"
              />
            </div>
          </div>

          <div className="date-presets">
            <span className="presets-label">Accesos rápidos:</span>
            <button className="preset-btn" onClick={setTodayFilter}>Hoy</button>
            <button className="preset-btn" onClick={setYesterdayFilter}>Ayer</button>
            <button className="preset-btn" onClick={setLastWeekFilter}>Última semana</button>
            <button className="preset-btn" onClick={setLastMonthFilter}>Último mes</button>
          </div>

          <button className="btn-primary btn-apply-filter" onClick={handleApplyFilter}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Aplicar Filtro
          </button>
        </div>

        {filterApplied && (
          <div className="filter-summary">
            <span className="filter-badge">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Filtro activo: {formatFilterRange()}
            </span>
            <span className="filter-result">
              {loading ? 'Cargando...' : `${susceptibleInvoices.length} facturas encontradas`}
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="loading">Cargando facturas susceptibles...</div>
      ) : susceptibleInvoices.length === 0 ? (
        <div className="card empty-card">
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12h6M12 9v6M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <h3>No hay facturas susceptibles</h3>
            <p>No se encontraron facturas marcadas como susceptibles de eliminación en el rango seleccionado.</p>
            <p className="hint">Intente ampliar el rango de fechas o verificar que existan facturas en ese período.</p>
          </div>
        </div>
      ) : (
        <>
          {/* Modo de Selección */}
          <div className="card">
            <h3>1. Seleccionar Tipo de Eliminación</h3>
            <div className="selection-modes">
              <label className={`mode-card ${selectionMode === 'amount' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="selectionMode"
                  value="amount"
                  checked={selectionMode === 'amount'}
                  onChange={() => setSelectionMode('amount')}
                />
                <div className="mode-content">
                  <h4>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline', verticalAlign: 'middle', marginRight: '6px'}}>
                      <line x1="12" y1="1" x2="12" y2="23"/>
                      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                    </svg>
                    Por Monto Específico
                  </h4>
                  <p>Eliminar hasta alcanzar un monto determinado</p>
                </div>
              </label>

              <label className={`mode-card ${selectionMode === 'percentage' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="selectionMode"
                  value="percentage"
                  checked={selectionMode === 'percentage'}
                  onChange={() => setSelectionMode('percentage')}
                />
                <div className="mode-content">
                  <h4>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline', verticalAlign: 'middle', marginRight: '6px'}}>
                      <line x1="18" y1="20" x2="18" y2="10"/>
                      <line x1="12" y1="20" x2="12" y2="4"/>
                      <line x1="6" y1="20" x2="6" y2="14"/>
                    </svg>
                    Por Porcentaje de Ventas
                  </h4>
                  <p>Eliminar un porcentaje del total</p>
                </div>
              </label>

              <label className={`mode-card ${selectionMode === 'count' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="selectionMode"
                  value="count"
                  checked={selectionMode === 'count'}
                  onChange={() => setSelectionMode('count')}
                />
                <div className="mode-content">
                  <h4>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline', verticalAlign: 'middle', marginRight: '6px'}}>
                      <line x1="4" y1="9" x2="20" y2="9"/>
                      <line x1="4" y1="15" x2="20" y2="15"/>
                      <line x1="10" y1="3" x2="8" y2="21"/>
                      <line x1="16" y1="3" x2="14" y2="21"/>
                    </svg>
                    Por Cantidad de Facturas
                  </h4>
                  <p>Eliminar un número específico de facturas</p>
                </div>
              </label>

              <label className={`mode-card ${selectionMode === 'manual' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="selectionMode"
                  value="manual"
                  checked={selectionMode === 'manual'}
                  onChange={() => setSelectionMode('manual')}
                />
                <div className="mode-content">
                  <h4>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline', verticalAlign: 'middle', marginRight: '6px'}}>
                      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
                    </svg>
                    Selección Manual
                  </h4>
                  <p>Seleccionar facturas individualmente</p>
                </div>
              </label>
            </div>
          </div>

          {/* Parámetros de Selección */}
          {selectionMode && (
            <div className="card">
              <h3>2. Configurar Parámetros</h3>

              {selectionMode === 'amount' && (
                <div className="parameter-form">
                  <div className="form-group">
                    <label>Monto a eliminar (COP):</label>
                    <input
                      type="number"
                      min="0"
                      step="1000"
                      value={targetAmount}
                      onChange={(e) => setTargetAmount(Number(e.target.value))}
                      placeholder="Ej: 1000000"
                      className="form-input"
                    />
                  </div>
                  <div className="parameter-info">
                    <p>Disponible en el rango: <strong>{formatCurrency(totalAvailable)}</strong></p>
                  </div>
                  <button className="btn-primary" onClick={handleApplySelection}>
                    Aplicar Selección
                  </button>
                </div>
              )}

              {selectionMode === 'percentage' && (
                <div className="parameter-form">
                  <div className="form-group">
                    <label>Porcentaje a eliminar (%):</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={targetPercentage}
                      onChange={(e) => setTargetPercentage(Number(e.target.value))}
                      placeholder="Ej: 30"
                      className="form-input"
                    />
                  </div>
                  <div className="parameter-info">
                    <p>Total disponible: <strong>{formatCurrency(totalAvailable)}</strong></p>
                    <p>{targetPercentage}% = <strong>{formatCurrency((totalAvailable * targetPercentage) / 100)}</strong></p>
                  </div>
                  <button className="btn-primary" onClick={handleApplySelection}>
                    Aplicar Selección
                  </button>
                </div>
              )}

              {selectionMode === 'count' && (
                <div className="parameter-form">
                  <div className="form-group">
                    <label>Cantidad de facturas a eliminar:</label>
                    <input
                      type="number"
                      min="0"
                      max={susceptibleInvoices.length}
                      value={targetCount}
                      onChange={(e) => setTargetCount(Number(e.target.value))}
                      placeholder={`Máximo: ${susceptibleInvoices.length}`}
                      className="form-input"
                    />
                  </div>
                  <div className="parameter-info">
                    <p>Facturas disponibles: <strong>{susceptibleInvoices.length}</strong></p>
                  </div>
                  <button className="btn-primary" onClick={handleApplySelection}>
                    Aplicar Selección
                  </button>
                </div>
              )}

              {selectionMode === 'manual' && (
                <div className="info-box">
                  <p>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline', verticalAlign: 'middle', marginRight: '6px'}}>
                      <path d="M9 18h6"/>
                      <path d="M10 22h4"/>
                      <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/>
                    </svg>
                    Seleccione manualmente las facturas en la tabla de abajo.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Resumen de Selección */}
          <div className="card selection-summary-card">
            <h3>3. Resumen de Selección</h3>
            <div className="selection-stats">
              <div className="stat-item">
                <span className="stat-label">Facturas en el rango:</span>
                <span className="stat-value">{susceptibleInvoices.length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Monto disponible:</span>
                <span className="stat-value">{formatCurrency(totalAvailable)}</span>
              </div>
              <div className="stat-item highlight">
                <span className="stat-label">Facturas seleccionadas:</span>
                <span className="stat-value">{selectedInvoices.size}</span>
              </div>
              <div className="stat-item highlight danger">
                <span className="stat-label">Monto a eliminar:</span>
                <span className="stat-value">{formatCurrency(calculateTotalSelected())}</span>
              </div>
            </div>
          </div>

          {/* Acciones */}
          <div className="action-bar authorization-actions">
            <button
              className="btn-secondary"
              onClick={handleSelectAll}
            >
              {selectedInvoices.size === susceptibleInvoices.length
                ? 'Deseleccionar Todas'
                : 'Seleccionar Todas'}
            </button>
            <button
              className="btn-danger btn-large"
              onClick={handleOpenConfirmModal}
              disabled={selectedInvoices.size === 0 || deleting}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline', verticalAlign: 'middle', marginRight: '6px'}}>
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              AUTORIZAR ELIMINACIÓN ({selectedInvoices.size} facturas)
            </button>
            <button
              className="btn-secondary"
              onClick={() => refetch()}
            >
              Actualizar
            </button>
          </div>

          {/* Tabla de facturas */}
          <div className="card">
            <h3>4. Facturas a Eliminar</h3>
            <div className="table-container">
              <table className="invoice-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={selectedInvoices.size === susceptibleInvoices.length && susceptibleInvoices.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>Número</th>
                    <th>Cliente</th>
                    <th>Documento</th>
                    <th>Total</th>
                    <th>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {susceptibleInvoices.map((invoice: any) => (
                    <tr key={invoice.id} className={selectedInvoices.has(invoice.id) ? 'selected' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedInvoices.has(invoice.id)}
                          onChange={() => handleToggleInvoice(invoice.id)}
                        />
                      </td>
                      <td className="invoice-number">{invoice.invoiceNumber}</td>
                      <td>{invoice.customerName}</td>
                      <td>{invoice.customerDocument}</td>
                      <td className="total">{formatCurrency(invoice.total)}</td>
                      <td>{formatDate(invoice.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Modal de Confirmación */}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
          <div className="modal authorization-modal" onClick={(e) => e.stopPropagation()}>
            <h3>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline', verticalAlign: 'middle', marginRight: '8px'}}>
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              CONFIRMACIÓN FINAL
            </h3>

            <div className="warning-box">
              <p><strong>ADVERTENCIA:</strong> Está a punto de eliminar permanentemente:</p>
              <ul>
                <li><strong>{selectedInvoices.size}</strong> facturas</li>
                <li>Monto total: <strong>{formatCurrency(calculateTotalSelected())}</strong></li>
                <li>Rango de fechas: <strong>{formatFilterRange()}</strong></li>
              </ul>
              <p>Esta acción NO se puede deshacer.</p>
            </div>

            <div className="form-group">
              <label>Motivo de eliminación (opcional):</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Puede ingresar un motivo si lo desea..."
                rows={3}
              />
            </div>

            <div className="modal-actions">
              <button
                className="btn-danger"
                onClick={handleAuthorize}
                disabled={deleting}
              >
                {deleting ? 'Procesando...' : 'CONFIRMAR ELIMINACIÓN'}
              </button>
              <button
                className="btn-secondary"
                onClick={() => setShowConfirmModal(false)}
                disabled={deleting}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sistema de Notificaciones */}
      <div className="notifications-container">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`notification notification-${notification.type}`}
          >
            <div className="notification-icon">
              {notification.type === 'success' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              )}
              {notification.type === 'error' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
              )}
              {notification.type === 'warning' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              )}
              {notification.type === 'info' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
              )}
            </div>
            <div className="notification-content">
              {notification.title && <span className="notification-title">{notification.title}</span>}
              <span className="notification-message">{notification.message}</span>
            </div>
            <button
              className="notification-close"
              onClick={() => closeNotification(notification.id)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        ))}
      </div>

      {/* Estilos para notificaciones */}
      <style>{`
        .notifications-container {
          position: fixed;
          top: 80px;
          right: 20px;
          z-index: 10000;
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-width: 400px;
        }

        .notification {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px 14px;
          border-radius: 6px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          animation: slideInNotification 0.3s ease-out;
        }

        @keyframes slideInNotification {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .notification-success {
          background: #eff6ff;
          border: 1px solid #3b82f6;
          border-left: 4px solid #2563eb;
          color: #1e40af;
        }

        .notification-error {
          background: #eff6ff;
          border: 1px solid #60a5fa;
          border-left: 4px solid #1d4ed8;
          color: #1e3a8a;
        }

        .notification-warning {
          background: #eff6ff;
          border: 1px solid #93c5fd;
          border-left: 4px solid #3b82f6;
          color: #1e40af;
        }

        .notification-info {
          background: #eff6ff;
          border: 1px solid #93c5fd;
          border-left: 4px solid #3b82f6;
          color: #1e40af;
        }

        .notification-icon {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
        }

        .notification-success .notification-icon { color: #2563eb; }
        .notification-error .notification-icon { color: #1d4ed8; }
        .notification-warning .notification-icon { color: #3b82f6; }
        .notification-info .notification-icon { color: #3b82f6; }

        .notification-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .notification-title {
          font-weight: 600;
          font-size: 0.9rem;
        }

        .notification-message {
          font-size: 0.85rem;
          line-height: 1.4;
        }

        .notification-close {
          flex-shrink: 0;
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0.6;
          transition: all 0.2s;
        }

        .notification-close:hover {
          opacity: 1;
          background: rgba(0, 0, 0, 0.1);
        }
      `}</style>
    </div>
  );
};
